import moment, { type Moment } from 'moment';
import type { FormInstance } from 'antd';
import type { JobInfo, Opening } from 'store/reducers/jobReducer';
import type { ServiceSimple, Uuid } from 'store/reducers/types';

interface LabelValue {
  value: string;
  label: string;
}

interface LabelValueBool {
  value: boolean;
  label: string;
}

interface ForeignLanguageFormRow {
  language?: LabelValue;
  level?: LabelValue;
}

export interface OpeningFormValues {
  team_lead: LabelValue;
  priority: LabelValue;
  expected: Moment;
  offices: LabelValue[];
  foreign_languages: ForeignLanguageFormRow[];
  employment: string;
  employment_custom_value?: string | number;
  work_experience: string;
  work_experience_custom_value?: string;
  experience_level: LabelValue;
  experience_level_custom_value?: string;
  technology_area?: LabelValue;
  requirements_responsibilities?: string;
  technical_requirements?: string;
  technical_responsibilities?: string;
  additional_requirements?: string;
  position_advantages?: string;
  comment?: string;
  is_affects_utilization: LabelValueBool;
}

type JobServiceWithLeads = ServiceSimple & {
  service_head?: { uuid: Uuid; full_name: string };
  team_leads?: Array<{ uuid: Uuid; full_name: string }>;
};

type OpeningForForm = Opening & {
  employment?: string;
  employment_custom_value?: string | number;
  work_experience?: string;
  work_experience_custom_value?: string;
  requirements_responsibilities?: string;
  technical_requirements?: string;
  technical_responsibilities?: string;
  additional_requirements?: string;
  position_advantages?: string;
  is_affects_utilization?: boolean;
  technology_area?: { uuid: Uuid; name: string; is_outdated?: boolean };
};

export const initialValuesMapper = (opening: OpeningForForm) => [
  { name: 'team_lead', value: { value: opening.team_lead.uuid, label: opening.team_lead.full_name } },
  { name: 'priority', value: { value: opening.priority, label: opening.priority } },
  { name: 'expected', value: moment(opening.expected, 'YYYY-MM-DD') },
  { name: 'offices', value: opening.offices!.map(o => ({ value: o.uuid, label: o.name })) },
  {
    name: 'foreign_languages',
    value: opening.foreign_languages!.length
      ? opening.foreign_languages!.map(o => ({
          language: { value: o.language, label: o.language },
          level: { value: o.level, label: o.level },
        }))
      : [{ language: undefined, level: undefined }],
  },
  { name: 'employment', value: opening.employment },
  { name: 'employment_custom_value', value: opening.employment_custom_value || '' },
  { name: 'work_experience', value: opening.work_experience },
  { name: 'work_experience_custom_value', value: opening.work_experience_custom_value || '' },
  { name: 'experience_level', value: { value: opening.level, label: opening.level } },
  { name: 'experience_level_custom_value', value: opening.custom_level },
  ...(opening.technology_area
    ? [
        {
          name: 'technology_area',
          value: {
            value: opening.technology_area.uuid,
            label: `${opening.technology_area.name}${opening.technology_area.is_outdated ? ' (Outdated)' : ''}`,
          },
        },
      ]
    : []),
  { name: 'requirements_responsibilities', value: opening.requirements_responsibilities },
  { name: 'requirements_responsibilities_initial', value: opening.requirements_responsibilities },
  { name: 'technical_requirements', value: opening.technical_requirements },
  { name: 'technical_requirements_initial', value: opening.technical_requirements },
  { name: 'technical_responsibilities', value: opening.technical_responsibilities },
  { name: 'technical_responsibilities_initial', value: opening.technical_responsibilities },
  { name: 'additional_requirements', value: opening.additional_requirements },
  { name: 'additional_requirements_initial', value: opening.additional_requirements },
  { name: 'position_advantages', value: opening.position_advantages },
  { name: 'position_advantages_initial', value: opening.position_advantages },
  { name: 'comment', value: opening.comment },
  { name: 'comment_initial', value: opening.comment },
  {
    name: 'is_affects_utilization',
    value: { value: opening.is_affects_utilization, label: opening.is_affects_utilization ? 'Yes' : 'No' },
  },
];

export const formValuesMapper = (data: OpeningFormValues) => ({
  team_lead: data.team_lead.value,
  priority: data.priority.value,
  expected: data.expected.format('YYYY-MM-DD'),
  offices: data.offices.map(el => el.value),
  foreign_languages: data.foreign_languages
    .filter(el => el.language && el.level)
    .map(el => ({ language: el.language!.value, level: el.level!.value })),
  employment: data.employment,
  employment_custom_value: +data.employment_custom_value! || 0,
  work_experience: data.work_experience,
  work_experience_custom_value: data.work_experience_custom_value,
  level: data.experience_level.value,
  custom_level: data.experience_level_custom_value,
  ...(data.technology_area ? { technology_area: data.technology_area.value } : {}),
  requirements_responsibilities: data.requirements_responsibilities,
  technical_requirements: data.technical_requirements,
  technical_responsibilities: data.technical_responsibilities,
  additional_requirements: data.additional_requirements,
  position_advantages: data.position_advantages,
  comment: data.comment,
  is_affects_utilization: data.is_affects_utilization.value,
});

export const teamLeadOptionsMapper = (jobData: JobInfo) => {
  const teamLeads: LabelValue[] = [];
  const service = jobData.service as JobServiceWithLeads | undefined;
  if (service?.service_head) {
    teamLeads.push({ value: service.service_head.uuid, label: service.service_head.full_name });
  }
  if (service?.team_leads) {
    service.team_leads.forEach(tl => {
      if (tl.uuid !== service.service_head?.uuid) {
        teamLeads.push({ value: tl.uuid, label: tl.full_name });
      }
    });
  }
  return teamLeads;
};

export const validatorL =
  (fieldToCheck: string, message: string, path = 'foreign_languages') =>
  (form: FormInstance, index: number) => ({
    validator: (_: unknown, v: unknown) => {
      const row = form.getFieldValue(path)[index];
      if (!row) return Promise.resolve();
      const field = row[fieldToCheck];
      if (field && !v) return Promise.reject(new Error(message));
      return Promise.resolve();
    },
    message,
  });
